import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import clsx from 'clsx';
import useDebounce from 'common/hooks/useDebounce';
import {
  useAssignAgentToProspect,
  useGetAgents
} from 'common/queries-and-mutations/admin/prospect';
import Button from 'components/Form/Button';
import SearchInput from 'components/Form/SearchInput';
import withCreatePortal from 'components/HOC/withCreatePortal';
import { Icon, Icons } from 'components/Icon';
import { Loader } from 'components/Loader';
import SuccessModal from 'components/Modal/SuccessModal';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import Paginator from 'components/TableWidget/Paginator';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import { AgList } from 'service/dto/prospect.dto';

const EnhancedSuccessModal = withCreatePortal(SuccessModal);

export default function AgentList() {
  const { prospectId } = useParams();
  const navigate = useNavigate();

  const [urlParams, setUrlParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>([]);
  const currentPage = urlParams.get('page') ?? '1';
  const numberOfItemsPerPage = urlParams.get('items_per_page') || 10;
  const searchQueryString = urlParams.get('search_query') ?? '';
  const debounceSearch = useDebounce(searchQueryString, 1300);
  const [isExecutingSearch, setIsExecutingSearch] = useState<boolean>(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate, isSuccess, isError } = useAssignAgentToProspect();
  const { data: agents, isFetching, isFetched, refetch } = useGetAgents();

  useEffect(() => {
    if (debounceSearch.length > 3) {
      urlParams.set('page', '1');
      urlParams.set('items_per_page', '10');
      setUrlParams(urlParams);
      setIsExecutingSearch(true);
    }
  }, [debounceSearch.length]);

  useEffect(() => {
    if (debounceSearch.length > 3) {
      setIsExecutingSearch(true);
    }
  }, [debounceSearch.length]);

  const columnHelper = createColumnHelper<AgList>();

  const columns = [
    columnHelper.accessor(row => row.agentName, {
      id: 'agentName',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Name</span>
      )
    }),
    columnHelper.accessor(row => row.email, {
      id: 'email',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">
          E-mail address
        </span>
      )
    }),
    columnHelper.accessor(row => row.mobileNo, {
      id: 'mobileNo',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">
          Phone Number
        </span>
      )
    }),
    columnHelper.accessor(row => row.agent_id, {
      id: 'agent_id',
      cell: info => (
        <div>
          {/* {isAssignToStaff(info.row.original.customers) ? (
            <td className="p-4 pl-5">
              <button
                type="button"
                className="font-medium text-[#E30000]"
                disabled={isSubmitting}
                onClick={() => {
                  // setBeneficiary({
                  //   beneficiaryId: beneficiary.id,
                  //   beneficiaryName: `${beneficiary?.beneficiaryFirstName} ${
                  //     beneficiary?.beneficiaryMiddleName || ''
                  //   } ${beneficiary?.beneficiaryLastName}`
                  // });
                  // setShowAskToConfirm(true);
                }}
              >
                Remove
              </button>
            </td>
          ) : (
            <td className="py-4 text-center">
              <Button
                variant="outline"
                size="custom"
                className="place-self-end rounded border-[#FF7A00] bg-[#FFF8F2] px-8 py-1.5 text-center text-xs font-medium text-[#FF7A00]"
                label="Assign"
                disabled={isSubmitting}
                loading={isSubmitting}
                onClick={() => {
                  setIsSubmitting(true);
                  mutate({
                    prospectId: prospectId!
                  });
                }}
              ></Button>
            </td>
          )} */}
          <td className="py-4 text-center">
            <Button
              variant="outline"
              size="custom"
              className="place-self-end rounded border-[#FF7A00] bg-[#FFF8F2] px-8 py-1.5 text-center text-xs font-medium text-[#FF7A00]"
              label="Assign"
              disabled={isSubmitting}
              loading={isSubmitting}
              onClick={() => {
                setIsSubmitting(true);
                mutate({
                  prospectId: prospectId!,
                  agentId: info.getValue()
                });
              }}
            ></Button>
          </td>
        </div>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Action</span>
      )
    })
  ];

  // const isAssignToStaff = (customers: StaffCustomer[]) => {
  //   const customerIds = (customers || []).map(customer => customer.customerId);
  //   return customerIds.includes(+customerId!);
  // };

  const totalPages = Math.ceil(
    (agents?.agList ?? []).length / Number(numberOfItemsPerPage)
  );
  const startIndex = (Number(currentPage) - 1) * Number(numberOfItemsPerPage);
  const endIndex = Math.min(
    startIndex + Number(numberOfItemsPerPage),
    (agents?.agList ?? []).length
  );

  const data = useMemo<AgList[]>(() => {
    if (isExecutingSearch) {
      return (agents?.agList ?? [])
        .filter(
          item =>
            item.agentName
              .toLowerCase()
              .includes(searchQueryString.toLowerCase()) ||
            item.mobileNo
              .toLowerCase()
              .includes(searchQueryString.toLowerCase()) ||
            item.email.toString().includes(searchQueryString.toLowerCase()) ||
            item.agent_id.toString().includes(searchQueryString.toLowerCase())
        )
        .slice(startIndex, endIndex)
        .map(item => ({
          agent_id: item.agent_id,
          agentName: item.agentName,
          mobileNo: item.mobileNo,
          email: item.email,
          suspended: item.suspended,
          activityCount: item.activityCount,
          prospectCount: item.prospectCount,
          vacation: item.vacation,
          prospectCount_manager: item.prospectCount_manager,
          prospectCount_lead: item.prospectCount_lead,
          prospectCount_self: item.prospectCount_self
        }));
    }
    return (agents?.agList ?? []).slice(startIndex, endIndex).map(item => ({
      agent_id: item.agent_id,
      agentName: item.agentName,
      mobileNo: item.mobileNo,
      email: item.email,
      suspended: item.suspended,
      activityCount: item.activityCount,
      prospectCount: item.prospectCount,
      vacation: item.vacation,
      prospectCount_manager: item.prospectCount_manager,
      prospectCount_lead: item.prospectCount_lead,
      prospectCount_self: item.prospectCount_self
    }));
  }, [
    isExecutingSearch,
    agents?.agList,
    startIndex,
    endIndex,
    searchQueryString
  ]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting
    },
    initialState: {
      pagination: {
        pageSize: +numberOfItemsPerPage
      }
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  const goToNextPage = () => {
    const nextPageValue = currentPage ? `${+currentPage + 1}` : '2';
    urlParams.set('page', nextPageValue);
    setUrlParams(urlParams);
  };

  const goToPreviousPage = () => {
    if (currentPage && +currentPage > 1) {
      const previousPageValue = `${+currentPage - 1}`;
      urlParams.set('page', previousPageValue);
      setUrlParams(urlParams);
    }
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    if (value.trim() === '') {
      urlParams.delete(name);
      urlParams.set('page', '1');
      urlParams.set('items_per_page', '10');
      setUrlParams(urlParams);
    }
    if (value.trim()) {
      urlParams.set(name, value);
      urlParams.set('page', '1');
      urlParams.set('items_per_page', '10');
      setUrlParams(urlParams);
    }
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      refetch();
      setIsSubmitting(false);
      setShowSuccess(true);
    }
    if (isError) {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  if (isFetching && isFetched) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <Breadcrumb title="Assign to Staff" />
        <Link
          to={`/admin/prospects/details/${prospectId}`}
          className="flex items-center gap-4"
        >
          <Icon name={Icons.ArrowLeft} />
          <span className="text-base font-bold text-primary-800">
            Back to Prospect&apos;s Profile
          </span>
        </Link>
      </div>

      <div className="mt-4 flex flex-col">
        <div className="h-fit w-full rounded-md bg-white pb-28 shadow-sm">
          <div className="border-b border-[#E5E5E5]">
            <div className="flex items-center justify-between p-3 px-5">
              <span className="text-xl font-medium text-[#353535]">
                Agent list
              </span>
              <div className="flex items-center gap-3">
                <SearchInput
                  name="search_query"
                  placeholder="Search for agent"
                  value={searchQueryString}
                  onChange={handleChange}
                  size="lg"
                  className="h-11 w-[20.1rem] rounded-[3px] border border-[#C3C3C3] bg-white"
                />
              </div>
            </div>
          </div>

          <div className="mt-3">
            <table
              className={clsx({
                'w-full min-w-full': true,
                'flexs flex-col h-[30rem]':
                  table.getRowModel().rows.length === 0
              })}
            >
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id} className="text-xs text-[#1F4173]">
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="p-5 text-left font-medium text-[#1F4173]"
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none flex'
                                : '',
                              onClick: header.column.getToggleSortingHandler()
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              // asc: (
                              //   <div className="flex flex-row items-center pl-2">
                              //     <Icon name={Icons.Sort} />
                              //   </div>
                              // ),
                              // desc: (
                              //   <div className="flex flex-row items-center pl-2">
                              //     <Icon name={Icons.Sort} />
                              //   </div>
                              // )
                              asc: <></>,
                              desc: <></>
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 && (
                  <tr className="mt-10 pt-10 text-center text-xl font-bold">
                    <td colSpan={7}>No agent found</td>
                  </tr>
                )}
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="">
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        className={clsx({
                          'px-5 text-xs border-b border-[#F3F5F6]': true
                        })}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-5 p-5">
              <Paginator
                numberOfPages={totalPages}
                page={+currentPage}
                hasNext={totalPages > Number(currentPage)}
                hasPrevious={Number(currentPage) > 1}
                numberOfItemsPerPage={+numberOfItemsPerPage}
                setNumberOfItemsPerPage={num => {
                  // setPageSize(num);
                  urlParams.set('items_per_page', num.toString());
                  setUrlParams(urlParams);
                }}
                goToNextPage={goToNextPage}
                goToPreviousPage={goToPreviousPage}
                showPageNumber={false}
              />
            </div>
          </div>
        </div>
      </div>

      {showSuccess && (
        <EnhancedSuccessModal
          title="Agent has been assigned Successfully"
          onProccessClick={() => {
            setShowSuccess(false);
            navigate(`/admin/prospects/details/${prospectId}`);
          }}
          buttonText="Back to Prospect"
        />
      )}
    </div>
  );
}
